import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { paymentService } from "../../services/paymentService";
import { getProfile } from "./authSlice";
import toast from "react-hot-toast";

const initialState = {
  transactions: [],
  orders: [],
  currentTransaction: null,
  enabledPaymentMethods: [],
  isLoading: false,
  error: null,
};

// Async thunks
export const initializeDeposit = createAsyncThunk(
  "payments/initializeDeposit",
  async (amount, { rejectWithValue }) => {
    try {
      const response = await paymentService.initializeDeposit(amount);
      window.location.href = response.authorization_url;
      return response;
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to initialize deposit",
      );
      return rejectWithValue(error.response?.data);
    }
  },
);

export const initializeKorapayDeposit = createAsyncThunk(
  "payments/initializeKorapayDeposit",
  async (amount, { rejectWithValue }) => {
    try {
      const response = await paymentService.initializeKorapayDeposit(amount);
      window.location.href = response.checkout_url;
      return response;
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to initialize Korapay deposit",
      );
      return rejectWithValue(error.response?.data);
    }
  },
);

export const initializeTransactpayDeposit = createAsyncThunk(
  "payments/initializeTransactpayDeposit",
  async (amount, { rejectWithValue }) => {
    try {
      const response =
        await paymentService.initializeTransactpayDeposit(amount);
      const payload = response?.data || response;
      const redirectUrl =
        payload?.payment_url ||
        payload?.paymentUrl ||
        payload?.checkout_url ||
        payload?.checkoutUrl ||
        payload?.redirectUrl ||
        payload?.authorization_url;

      if (redirectUrl) {
        window.location.href = redirectUrl;
      }

      return response;
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Failed to initialize Transactpay deposit",
      );
      return rejectWithValue(error.response?.data);
    }
  },
);

export const verifyKorapayPayment = createAsyncThunk(
  "payments/verifyKorapayPayment",
  async (reference, { rejectWithValue }) => {
    try {
      const response = await paymentService.verifyKorapayPayment(reference);
      toast.success("Payment verified successfully! 💰");
      return response;
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Payment verification failed",
      );
      return rejectWithValue(error.response?.data);
    }
  },
);

export const fetchEnabledPaymentMethods = createAsyncThunk(
  "payments/fetchEnabledPaymentMethods",
  async (_, { rejectWithValue }) => {
    try {
      const response = await paymentService.getEnabledPaymentMethods();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const verifyPayment = createAsyncThunk(
  "payments/verifyPayment",
  async (reference, { rejectWithValue }) => {
    try {
      const response = await paymentService.verifyPayment(reference);
      toast.success("Payment verified successfully! 💰");
      return response;
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Payment verification failed",
      );
      return rejectWithValue(error.response?.data);
    }
  },
);

export const purchaseAccount = createAsyncThunk(
  "payments/purchaseAccount",
  async (accountId, { dispatch, rejectWithValue }) => {
    try {
      const response = await paymentService.purchaseAccount(accountId);
      await dispatch(getProfile())
        .unwrap()
        .catch(() => null);
      toast.success("Account purchased successfully! 🎉");
      return response;
    } catch (error) {
      toast.error(error.response?.data?.message || "Purchase failed");
      return rejectWithValue(error.response?.data);
    }
  },
);
export const fetchTransactions = createAsyncThunk(
  "payments/fetchTransactions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await paymentService.getTransactions();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const fetchOrders = createAsyncThunk(
  "payments/fetchOrders",
  async (_, { rejectWithValue }) => {
    try {
      const response = await paymentService.getOrders();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

const paymentSlice = createSlice({
  name: "payments",
  initialState,
  reducers: {
    clearCurrentTransaction: (state) => {
      state.currentTransaction = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Initialize Deposit
      .addCase(initializeDeposit.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(initializeDeposit.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentTransaction = action.payload.transaction;
      })
      .addCase(initializeDeposit.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Initialize Korapay Deposit
      .addCase(initializeKorapayDeposit.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(initializeKorapayDeposit.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentTransaction = action.payload.transaction;
      })
      .addCase(initializeKorapayDeposit.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Initialize Transactpay Deposit
      .addCase(initializeTransactpayDeposit.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(initializeTransactpayDeposit.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentTransaction = action.payload.transaction;
      })
      .addCase(initializeTransactpayDeposit.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Verify Korapay Payment
      .addCase(verifyKorapayPayment.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(verifyKorapayPayment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentTransaction = action.payload.transaction;
      })
      .addCase(verifyKorapayPayment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch enabled payment methods
      .addCase(fetchEnabledPaymentMethods.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchEnabledPaymentMethods.fulfilled, (state, action) => {
        state.isLoading = false;
        state.enabledPaymentMethods = action.payload.data;
      })
      .addCase(fetchEnabledPaymentMethods.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch Transactions
      .addCase(fetchTransactions.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.transactions = action.payload.transactions;
      })
      .addCase(fetchTransactions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch Orders
      .addCase(fetchOrders.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.isLoading = false;
        state.orders = action.payload.orders;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Purchase Account
      .addCase(purchaseAccount.fulfilled, (state, action) => {
        state.orders.unshift(action.payload.order);
      });
  },
});

export const { clearCurrentTransaction } = paymentSlice.actions;
export default paymentSlice.reducer;
